import { c as _c } from "react/compiler-runtime";
import { watch } from "@arcgis/core/core/reactiveUtils";
import { isPointOnVisibleGlobeSide, projectPointToScreen } from "@components/world-map/world-map-utils";
import { useEffect, useState } from "react";

/** Screen position (px) of a projected map point. */

/** Returns whether two screen positions are equal (both null counts as equal). */
function areSamePosition(a, b) {
  if (a === null || b === null) {
    return a === b;
  }
  return a.x === b.x && a.y === b.y;
}

/**
 * Projects a highlighted marker's map point to a screen position and keeps it in
 * sync as the camera moves, so the DOM pulse ring can stay glued to the marker in
 * both 2D and 3D. Returns null when there is no point to track or the point is
 * off-screen / behind the globe.
 */
export function useMarkerPulseOverlay(viewRef, isViewReady, mapPoint) {
  const $ = _c(5);
  const [position, setPosition] = useState(null);
  let t0;
  let t1;
  if ($[0] !== isViewReady || $[1] !== mapPoint || $[2] !== viewRef) {
    t0 = () => {
      const view = viewRef.current;
      if (!isViewReady || !view || !mapPoint) {
        setPosition(null);
        return;
      }
      const reproject = function reproject() {
        const currentView = viewRef.current;
        if (!currentView || !mapPoint) {
          setPosition(null);
          return;
        }
        const next = isPointOnVisibleGlobeSide(currentView, mapPoint) ? projectPointToScreen(currentView, mapPoint) : null;
        setPosition(prev => areSamePosition(prev, next) ? prev : next);
      };
      reproject();
      const handle = watch(() => view.type === "3d" ? view.camera : view.viewpoint, reproject);
      return () => handle.remove();
    };
    t1 = [viewRef, isViewReady, mapPoint];
    $[0] = isViewReady;
    $[1] = mapPoint;
    $[2] = viewRef;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return position;
}